import axios, { AxiosInstance } from 'axios';

import getRequestConfig from '@/api/interceptors/getRequestConfig';
import { getResponseError } from '@/api/interceptors/getResponseError';
import getResponseErrorType from '@/api/interceptors/getResponseErrorType';
import { refreshedCredentialsNative } from '@/utils/authService';
import { isInCapacitorApp } from '@/utils/device';
import { secureStorageSet } from '@/utils/native/nativeBridge';

const isNative = isInCapacitorApp();
let isMobileRequestRetried = false;

const baseURL =
  isNative || window.location.origin.includes('localhost')
    ? import.meta.env.VITE_AXIOS_BASE_URL || 'https://dev-web-sconchat.sconai.com'
    : window.location.origin;

const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
// DNS/network 이슈인지(Failed to load resource: net::ERR_NAME_NOT_RESOLVED)
const isTransientDns = (err: any) => {
  const msg = String(err?.message || '').toUpperCase();
  const code = String(err?.code || '').toUpperCase();
  return (
    !err?.response &&
    (code === 'ERR_NAME_NOT_RESOLVED' || msg.includes('ERR_NAME_NOT_RESOLVED') || code === 'ERR_NETWORK')
  );
};

// 요청 인터셉터
api.interceptors.request.use(
  (config) => {
    const { url = '' } = config;

    const pathname = new URL(url, window.location.origin).pathname;

    const isAppSignup = /^\/api\/auth\/3rd\/signup\/[^/]+\/app$/.test(pathname);

    const isLoginOrSignup = url.includes('login') || isAppSignup;
    return isLoginOrSignup ? config : getRequestConfig(config);
  },
  (error) => Promise.reject(error)
);

// 답변 인터셉터
api.interceptors.response.use(
  (res) => {
    isMobileRequestRetried = false;
    return { ...res, success: true };
  },
  async (err) => {
    const { config: { url = '' } = { url: '' } } = err;

    const cfg = err?.config || {};

    // DNS/network 이슈일 경우 0.5초마다 재시도 (최대 3회)
    if (isTransientDns(err)) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= 3) {
        await sleep(500);
        return api.request(cfg);
      }
    }

    if (isNative && err?.response?.status === 401 && !isMobileRequestRetried) {
      isMobileRequestRetried = true;

      try {
        const credentials = await refreshedCredentialsNative();
        const { accessToken } = credentials;

        await secureStorageSet({ key: 'authToken', value: accessToken });

        return api.request(cfg);
      } catch (error) {
        console.error('모바일 401 에러 재인증 절차가 실패했습니다', error);
        isMobileRequestRetried = false;
      }
    }

    const resError = getResponseError(err);
    switch (getResponseErrorType(err, resError)) {
      case 'REJECT':
        return Promise.reject(resError);
      case 'RESOLVE':
        return Promise.resolve();
      default:
        return resError;
    }
  }
);

export default api;
